import React, { useState, useEffect, useCallback } from 'react';
import { Select } from 'antd';
import { debounce } from 'lodash';
import { apiGetCategory } from '@services/ManageDevices/DeviceServices';

const { Option } = Select;

const ParentDeviceSelect = ({
  value,
  onChange,
  parentBranch,
  parentManageGroup,
  showTitle = true
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const fetchParentDevices = async (search = '') => {
    setLoading(true);
    try {
      const response = await apiGetCategory({
        keyword: search === '' ? undefined : search,
        type: 'DHK',
        parent_level_1_id: parentManageGroup ?? undefined
        // parent_level_2_id: parentBranch ?? undefined
      });

      // Group devices by name
      const groupedByName = {};
      response.data?.data?.forEach((item) => {
        const name = item.name;
        if (!groupedByName[name]) {
          groupedByName[name] = [];
        }
        groupedByName[name].push(item.value);
      });

      // Create options with unique names and array of codes
      const uniqueOptions = Object.entries(groupedByName).map(([name, codes]) => ({
        label: name,
        value: name, // Use name as value for Select
        codes: codes, // Keep all codes for reference
        isMultiple: codes.length > 1
      }));

      setOptions(
        uniqueOptions.sort((a, b) =>
          a?.label?.localeCompare(b?.label, 'vi', { sensitivity: 'base' })
        )
      );
    } catch (error) {
      console.error('Error fetching parent device data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tạo debounced function với useCallback để tránh tạo lại function mỗi lần render
  const debouncedFetchParentDevices = useCallback(
    debounce((searchText) => {
      fetchParentDevices(searchText);
    }, 800),
    [parentManageGroup]
  );

  useEffect(() => {
    fetchParentDevices();
  }, [parentBranch, parentManageGroup]);

  const handleChange = (selectedLabels) => {
    // selectedLabels is array of label names (strings)
    // Convert to array of all codes
    const allCodes = [];
    selectedLabels.forEach((label) => {
      const option = options.find((opt) => opt.value === label);
      if (option) {
        allCodes.push(...option.codes);
      }
    });
    onChange(allCodes);
    // Clear search text after selection
    if (searchText) {
      setSearchText('');
      fetchParentDevices();
    }
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    if (searchValue === '') {
      // Nếu xóa hết keyword, gọi API ngay lập tức
      fetchParentDevices();
    } else {
      debouncedFetchParentDevices(searchValue);
    }
  };

  // Convert value (array of codes) to selected labels (array of strings)
  const getSelectedValues = () => {
    if (!value || value.length === 0) return [];

    // Find which options contain the selected codes
    const selectedLabels = [];
    options.forEach((option) => {
      // Check if any code from this option is in value
      if (option.codes.some((code) => value.includes(code))) {
        selectedLabels.push(option.value); // option.value is the label now
      }
    });
    return selectedLabels;
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      {showTitle && (
        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
          Đồng hồ khối
        </label>
      )}
      <Select
        mode="multiple"
        value={getSelectedValues()}
        onChange={handleChange}
        placeholder="Chọn đồng hồ khối"
        style={{ width: '100%', height: '36px' }}
        showSearch
        filterOption={false}
        onSearch={handleSearch}
        searchValue={searchText}
        loading={loading}
        allowClear
        maxTagCount="responsive"
        // disabled={!parentManageGroup || parentManageGroup.length === 0}
      >
        {options.map((option, index) => (
          <Option key={`${option.label}-${index}`} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default ParentDeviceSelect;
